// Rich session card: route drawing (lazily fetched from the activity's
// detail JSON when the summary lacks one) + the key stats.
import { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Flex, Grid, Link, Text } from "@chakra-ui/react";
import { FG, GRID, INSET, MONO, MUTED, PANEL, alpha } from "../theme";
import { SportIcon, TrackSvg, fmtDate, geoToTrack, sportColor } from "./ui";

const trackCache = {};

export const useCardTrack = (act) => {
  const [track, setTrack] = useState(act.track || trackCache[act.id] || null);
  useEffect(() => {
    if (act.track || !act.id || !act.detail) return undefined;
    if (trackCache[act.id] !== undefined) {
      setTrack(trackCache[act.id]);
      return undefined;
    }
    let alive = true;
    fetch(`${process.env.PUBLIC_URL}/activities/${act.id}.json`)
      .then((r) => r.json())
      .then((d) => {
        const t = d.track || geoToTrack(d.geo);
        trackCache[act.id] = t || null;
        if (alive && t) setTrack(t);
      })
      .catch(() => {
        trackCache[act.id] = null;
      });
    return () => {
      alive = false;
    };
  }, [act.id, act.track, act.detail]);
  return track;
};

const ActivityCard = ({ act }) => {
  const color = sportColor(act.group);
  const track = useCardTrack(act);
  const stats = [
    ["time", act.dur],
    act.km >= 1 && ["km", `${act.km}`],
    act.avgHR && ["avg hr", `${Math.round(act.avgHR)}`],
    act.elev > 10 ? ["elev m", `${act.elev}`] : act.cal && ["kcal", `${act.cal}`],
  ]
    .filter(Boolean)
    .slice(0, 4);

  const inner = (
    <Box
      bg={PANEL}
      border={`1px solid ${GRID}`}
      borderRadius="xl"
      p={3}
      h="100%"
      cursor={act.id ? "pointer" : "default"}
      transition="transform .4s cubic-bezier(0.22, 1, 0.36, 1), border-color .3s"
      role="group"
      _hover={act.id ? { transform: "translateY(-4px)", borderColor: alpha(color, 0.5) } : {}}
    >
      <Flex
        h={{ base: "110px", md: "130px" }}
        mb={3}
        align="center"
        justify="center"
        bg={INSET}
        borderRadius="lg"
        p={3}
        position="relative"
        overflow="hidden"
        bgImage={`radial-gradient(circle at 50% 50%, ${alpha(color, 0.08)}, transparent 70%)`}
      >
        {track ? (
          <TrackSvg track={track} stroke={color} strokeWidth={0.045} glow />
        ) : (
          <SportIcon group={act.group} size={40} />
        )}
        <Box position="absolute" top={2} left={2}>
          <SportIcon group={act.group} size={14} />
        </Box>
      </Flex>
      <Box px={1}>
        <Text color={FG} fontWeight={600} fontSize="sm" noOfLines={1} letterSpacing="-0.01em">
          {act.name}
        </Text>
        <Text fontFamily={MONO} fontSize="10px" letterSpacing="0.08em" textTransform="uppercase" color={MUTED} mb={2.5} mt={0.5}>
          {fmtDate(act.date)} · <Text as="span" color={color}>{act.group}</Text>
        </Text>
        <Grid templateColumns={`repeat(${Math.min(stats.length, 4)}, 1fr)`} gap={1} borderTop={`1px solid ${GRID}`} pt={2}>
          {stats.map(([l, v]) => (
            <Box key={l} minW={0}>
              <Text color={FG} fontSize="sm" fontWeight={600} lineHeight="1.2" noOfLines={1}>
                {v}
              </Text>
              <Text color={MUTED} fontSize="9px" fontFamily={MONO} textTransform="uppercase" letterSpacing="0.06em">
                {l}
              </Text>
            </Box>
          ))}
        </Grid>
      </Box>
    </Box>
  );
  return act.id ? (
    <Link as={RouterLink} to={`/fitness/a/${act.id}`} _hover={{ textDecoration: "none" }} display="block" h="100%">
      {inner}
    </Link>
  ) : (
    inner
  );
};

export default ActivityCard;
